import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
	ArrowRight,
	Star,
	Phone,
	Calendar,
	ChevronLeft,
	ChevronRight,
	Quote,
	FileText,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import PDFViewerModal from "../components/PDFViewerModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pdfLinks = {
	petitDejeuner: "/pdf/Carte_petit_dej_Brasserie_Carougeoise.pdf",
	carte: "/pdf/Carte_des_mets_brasserie_carougeoise.pdf",
	vins: "/pdf/Carte_des_vins_brasserie_carougeoise.pdf",
	desserts: "/pdf/Carte_des_Desserts_brasserie_carougeoise.pdf",
	cocktailsTapas: "/pdf/Carte_apero_Brasserie_Carougeoise.pdf",
	menuDuJour: "/pdf/menu_du_jour_du_16_au_22.pdf",
};

// Hero Section
const HeroSection = () => {
	const { t } = useTranslation();

	return (
		<section
			className="relative h-screen min-h-[700px] flex items-center"
			data-testid="hero-section"
		>
			<div className="absolute inset-0">
				<img
					src="/images/ambiance/hero3.png"
					alt={t("hero.image_alt")}
					className="w-full h-full object-cover"
				/>
				<div className="absolute inset-0 hero-overlay" />
			</div>

			<div className="relative container mx-auto px-4 md:px-6 max-w-7xl">
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.2 }}
					className="max-w-3xl"
				>
					<motion.span
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.4 }}
						className="inline-block text-gold font-accent text-lg md:text-xl tracking-widest mb-4"
					>
						{t("hero.subtitle")}
					</motion.span>

					<motion.h1
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.5 }}
						className="font-heading text-5xl md:text-7xl lg:text-8xl text-white font-bold mb-4"
					>
						{t("hero.title")}
					</motion.h1>

					<motion.p
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.6 }}
						className="text-white/90 font-accent text-xl md:text-2xl italic mb-6"
					>
						{t("hero.tagline")}
					</motion.p>

					<motion.p
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.7 }}
						className="text-white/70 text-base md:text-lg max-w-xl mb-8 font-body"
					>
						{t("hero.description")}
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.8 }}
						className="flex flex-col sm:flex-row gap-4"
					>
						<Link to="/reservation">
							<Button
								size="lg"
								className="bg-gold hover:bg-gold/90 text-white rounded-none px-8 py-6 text-sm tracking-wider uppercase btn-hover-expand"
								data-testid="hero-reserve-btn"
							>
								<Calendar className="mr-2 h-4 w-4" />
								{t("hero.cta_reserve")}
							</Button>
						</Link>
						<Link to="/menu">
							<Button
								size="lg"
								variant="outline"
								className="border-white text-white hover:bg-white hover:text-primary rounded-none px-8 py-6 text-sm tracking-wider uppercase"
								data-testid="hero-menu-btn"
							>
								{t("hero.cta_menu")}
								<ArrowRight className="ml-2 h-4 w-4" />
							</Button>
						</Link>
					</motion.div>
				</motion.div>
			</div>

			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 1.2 }}
				className="absolute bottom-8 left-1/2 -translate-x-1/2"
			>
				<motion.div
					animate={{ y: [0, 10, 0] }}
					transition={{ repeat: Infinity, duration: 1.5 }}
					className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2"
				>
					<div className="w-1 h-2 bg-white/70 rounded-full" />
				</motion.div>
			</motion.div>
		</section>
	);
};

// Timeline / Service Hours Section
const DayTimelineSection = () => {
	const { t } = useTranslation();

	const moments = [
		{
			key: "breakfast",
			time: t("timeline.breakfast.time"),
			title: t("timeline.breakfast.title"),
			image: "/images/image-petit-dejeuner.jpg",
			alt: t("timeline.breakfast.alt"),
			description: t("timeline.breakfast.description"),
			buttons: [
				{
					label: t("timeline.breakfast.buttons.breakfast_menu"),
					title: t("timeline.breakfast.buttons.breakfast_menu_title"),
					pdfUrl: pdfLinks.petitDejeuner,
					variant: "gold",
				},
			],
		},
		{
			key: "lunch",
			time: t("timeline.lunch.time"),
			title: t("timeline.lunch.title"),
			image: "/images/image-dejeuner.jpg",
			alt: t("timeline.lunch.alt"),
			description: t("timeline.lunch.description"),
			highlight: {
				label: t("menu.daily_menu"),
				description: t("timeline.highlight.description"),
				pdfUrl: pdfLinks.menuDuJour,
			},
			buttons: [
				{
					label: t("timeline.lunch.buttons.food_menu"),
					title: t("timeline.lunch.buttons.food_menu_title"),
					pdfUrl: pdfLinks.carte,
					variant: "primary",
				},
				{
					label: t("timeline.lunch.buttons.wine_menu"),
					title: t("timeline.lunch.buttons.wine_menu_title"),
					pdfUrl: pdfLinks.vins,
					variant: "gold",
				},
			],
		},
		{
			key: "snack",
			time: t("timeline.snack.time"),
			title: t("timeline.snack.title"),
			image: "/images/image-gouter.jpg",
			alt: t("timeline.snack.alt"),
			description: t("timeline.snack.description"),
			buttons: [
				{
					label: t("timeline.snack.buttons.dessert_menu"),
					title: t("timeline.snack.buttons.dessert_menu_title"),
					pdfUrl: pdfLinks.desserts,
					variant: "primary",
				},
			],
		},
		{
			key: "aperitif",
			time: t("timeline.aperitif.time"),
			title: t("timeline.aperitif.title"),
			image: "/images/image-apero.jpg",
			alt: t("timeline.aperitif.alt"),
			description: t("timeline.aperitif.description"),
			buttons: [
				{
					label: t("timeline.aperitif.buttons.cocktail_tapas_menu"),
					title: t("timeline.aperitif.buttons.cocktail_tapas_menu_title"),
					pdfUrl: pdfLinks.cocktailsTapas,
					variant: "gold",
				},
			],
		},
		{
			key: "dinner",
			time: t("timeline.dinner.time"),
			title: t("timeline.dinner.title"),
			image: "/images/image-diner.jpg",
			alt: t("timeline.dinner.alt"),
			description: t("timeline.dinner.description"),
			buttons: [
				{
					label: t("timeline.dinner.buttons.food_menu"),
					title: t("timeline.dinner.buttons.food_menu_title"),
					pdfUrl: pdfLinks.carte,
					variant: "primary",
				},
				{
					label: t("timeline.dinner.buttons.wine_menu"),
					title: t("timeline.dinner.buttons.wine_menu_title"),
					pdfUrl: pdfLinks.vins,
					variant: "gold",
				},
			],
		},
	];

	const buttonClass = {
		primary:
			"gap-2 rounded-none border-primary text-primary hover:bg-primary hover:text-white",
		gold: "gap-2 rounded-none border-gold text-gold hover:bg-gold hover:text-white",
	};

	return (
		<section
			className="py-20 md:py-32 bg-background paper-texture"
			data-testid="day-timeline-section"
		>
			<div className="container mx-auto px-4 md:px-6 max-w-7xl">
				<motion.div
					initial={{ opacity: 0, y: 24 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					className="text-center mb-16"
				>
					<span className="text-gold font-accent text-lg tracking-widest">
						{t("timeline.subtitle")}
					</span>
					<h2 className="font-heading text-4xl md:text-5xl text-primary mt-2 mb-4">
						{t("timeline.title")}
					</h2>
					<div className="section-divider mx-auto" />
					<p className="text-muted-foreground max-w-2xl mx-auto font-body text-lg">
						{t("timeline.description")}
					</p>
				</motion.div>

				<div className="space-y-16 md:space-y-24">
					{moments.map((moment, index) => (
						<motion.div
							key={moment.key}
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: index * 0.08 }}
							className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
						>
							<div className={index % 2 !== 0 ? "lg:order-2" : ""}>
								<div className="aspect-[4/3] overflow-hidden bg-secondary">
									<img
										src={moment.image}
										alt={moment.alt}
										className="w-full h-full object-cover img-zoom"
									/>
								</div>
							</div>

							<div className={index % 2 !== 0 ? "lg:order-1" : ""}>
								<span className="inline-block text-gold font-accent text-sm md:text-base tracking-[0.2em] uppercase mb-3">
									{moment.time}
								</span>

								<h3 className="font-heading text-3xl md:text-4xl text-primary mb-4">
									{moment.title}
								</h3>

								<div className="w-16 h-0.5 bg-gold mb-6" />

								<p className="text-muted-foreground text-lg leading-relaxed font-body mb-6">
									{moment.description}
								</p>

								{moment.highlight && (
									<div className="mb-8 p-5 border border-gold/40 bg-gold/5">
										<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
											<div>
												<p className="text-gold font-accent uppercase tracking-widest text-sm mb-1">
													{moment.highlight.label}
												</p>
												<p className="text-primary font-body">
													{moment.highlight.description}
												</p>
											</div>

											<PDFViewerModal
												title={t("timeline.highlight.menu_of_the_day_title")}
												pdfUrl={moment.highlight.pdfUrl}
											>
												<Button
													variant="outline"
													className="gap-2 rounded-none border-gold text-gold hover:bg-gold hover:text-white"
												>
													<FileText className="h-4 w-4" />
													{t("timeline.highlight.view_menu")}
												</Button>
											</PDFViewerModal>
										</div>
									</div>
								)}

								<div className="flex flex-wrap gap-4">
									{moment.buttons.map((button) => (
										<PDFViewerModal
											key={button.label}
											title={button.title}
											pdfUrl={button.pdfUrl}
										>
											<Button
												variant="outline"
												className={buttonClass[button.variant]}
											>
												<FileText className="h-4 w-4" />
												{button.label}
											</Button>
										</PDFViewerModal>
									))}
								</div>
							</div>
						</motion.div>
					))}
				</div>
			</div>
		</section>
	);
};

// About Section
const AboutSection = () => {
	const { t } = useTranslation();

	return (
		<section
			className="py-20 md:py-32 bg-background relative paper-texture"
			data-testid="about-section"
		>
			<div className="container mx-auto px-4 md:px-6 max-w-7xl">
				<div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
					<motion.div
						initial={{ opacity: 0, x: -30 }}
						whileInView={{ opacity: 1, x: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6 }}
						className="relative"
					>
						<div className="aspect-[4/5] overflow-hidden">
							<img
								src="/images/plats/roti.jpg"
								alt={t("about.image_alt")}
								className="w-full h-full object-cover img-zoom"
							/>
						</div>
						<div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gold flex items-center justify-center">
							<span className="font-heading text-white text-4xl font-bold">
								1958
							</span>
						</div>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, x: 30 }}
						whileInView={{ opacity: 1, x: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6, delay: 0.2 }}
					>
						<span className="text-gold font-accent text-lg tracking-widest">
							{t("about.subtitle")}
						</span>
						<h2 className="font-heading text-4xl md:text-5xl text-primary mt-2 mb-6">
							{t("about.title")}
						</h2>
						<div className="w-20 h-0.5 bg-gold mb-8" />

						<p className="text-muted-foreground text-lg leading-relaxed mb-6 font-body">
							{t("about.description1")}
						</p>
						<p className="text-muted-foreground text-lg leading-relaxed mb-8 font-body">
							{t("about.description2")}
						</p>

						<blockquote className="border-l-4 border-gold pl-6 italic text-primary font-accent text-xl">
							"{t("about.philosophy")}"
						</blockquote>

						<Link to="/restaurant" className="inline-block mt-8">
							<Button
								variant="outline"
								className="border-primary text-primary hover:bg-primary hover:text-white rounded-none px-6 py-5 text-sm tracking-wider uppercase"
							>
								{t("about.cta")}
								<ArrowRight className="ml-2 h-4 w-4" />
							</Button>
						</Link>
					</motion.div>
				</div>
			</div>
		</section>
	);
};

// Featured Dishes Section
const FeaturedDishes = () => {
	const { t } = useTranslation();

	const dishes = [
		{ key: "choucroute", image: "/images/plats/choucroute.jpg" },
		{ key: "voluvent", image: "/images/plats/vol_au_vent.jpg" },
		{ key: "moules", image: "/images/plats/moules.png" },
		{ key: "cocotte", image: "/images/plats/cocotte_de_veau.jpg" },
	];

	return (
		<section
			className="py-20 md:py-32 bg-secondary"
			data-testid="dishes-section"
		>
			<div className="container mx-auto px-4 md:px-6 max-w-7xl">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					className="text-center mb-16"
				>
					<span className="text-gold font-accent text-lg tracking-widest">
						{t("dishes.subtitle")}
					</span>
					<h2 className="font-heading text-4xl md:text-5xl text-primary mt-2 mb-4">
						{t("dishes.title")}
					</h2>
					<div className="section-divider mx-auto" />
				</motion.div>

				<div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
					{dishes.map((dish, index) => (
						<motion.div
							key={dish.key}
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: index * 0.1 }}
							className="group"
						>
							<div className="aspect-square overflow-hidden bg-white card-elegant">
								<img
									src={dish.image}
									alt={t(`dishes.${dish.key}`)}
									className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
								/>
							</div>
							<h3 className="font-heading text-xl text-primary mt-4 text-center group-hover:text-gold transition-colors">
								{t(`dishes.${dish.key}`)}
							</h3>
						</motion.div>
					))}
				</div>

				<motion.div
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true }}
					className="text-center mt-12"
				>
					<Link to="/menu">
						<Button
							className="bg-primary text-white hover:bg-primary/90 rounded-none px-8 py-6 text-sm tracking-wider uppercase btn-hover-expand"
							data-testid="dishes-menu-btn"
						>
							{t("hero.cta_menu")}
							<ArrowRight className="ml-2 h-4 w-4" />
						</Button>
					</Link>
				</motion.div>
			</div>
		</section>
	);
};

// Reviews Section
const ReviewsSection = () => {
	const { t } = useTranslation();
	const [activeIndex, setActiveIndex] = useState(0);

	const reviews = [
		{ text: t("reviews.review1"), author: t("reviews.author1") },
		{ text: t("reviews.review2"), author: t("reviews.author2") },
		{ text: t("reviews.review3"), author: t("reviews.author3") },
	];

	const nextReview = () =>
		setActiveIndex((prev) => (prev + 1) % reviews.length);
	const prevReview = () =>
		setActiveIndex((prev) => (prev - 1 + reviews.length) % reviews.length);

	return (
		<section
			className="py-20 md:py-32 bg-primary text-white"
			data-testid="reviews-section"
		>
			<div className="container mx-auto px-4 md:px-6 max-w-7xl">
				<div className="grid lg:grid-cols-2 gap-12 items-center">
					<motion.div
						initial={{ opacity: 0, x: -30 }}
						whileInView={{ opacity: 1, x: 0 }}
						viewport={{ once: true }}
					>
						<span className="text-gold font-accent text-lg tracking-widest">
							{t("reviews.subtitle")}
						</span>
						<h2 className="font-heading text-4xl md:text-5xl mt-2 mb-8">
							{t("reviews.title")}
						</h2>

						<div className="flex items-center gap-4 mb-4">
							<span className="text-6xl md:text-7xl font-heading font-bold">
								4.3
							</span>
							<div>
								<div className="flex gap-1 mb-1">
									{[1, 2, 3, 4].map((i) => (
										<Star key={i} className="h-6 w-6 fill-gold text-gold" />
									))}
									<Star className="h-6 w-6 text-gold/30" />
								</div>
								<span className="text-white/70 text-sm">
									{t("reviews.rating")}
								</span>
							</div>
						</div>

						<p className="text-white/70 text-lg">
							<span className="text-gold font-semibold">295+</span>{" "}
							{t("reviews.reviews_count")}
						</p>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, x: 30 }}
						whileInView={{ opacity: 1, x: 0 }}
						viewport={{ once: true }}
						className="relative"
					>
						<div className="bg-white/10 p-8 md:p-12 rounded-sm relative">
							<Quote className="absolute top-6 left-6 h-10 w-10 text-gold/30" />

							<div className="min-h-[150px]">
								<p className="text-xl md:text-2xl font-accent italic leading-relaxed mb-6">
									"{reviews[activeIndex].text}"
								</p>
								<p className="text-gold font-semibold">
									— {reviews[activeIndex].author}
								</p>
							</div>

							<div className="flex gap-4 mt-8">
								<button
									onClick={prevReview}
									className="p-2 border border-white/30 hover:border-gold hover:text-gold transition-colors"
									data-testid="review-prev-btn"
									aria-label={t("reviews.previous")}
								>
									<ChevronLeft className="h-5 w-5" />
								</button>
								<button
									onClick={nextReview}
									className="p-2 border border-white/30 hover:border-gold hover:text-gold transition-colors"
									data-testid="review-next-btn"
									aria-label={t("reviews.next")}
								>
									<ChevronRight className="h-5 w-5" />
								</button>
							</div>
						</div>
					</motion.div>
				</div>
			</div>
		</section>
	);
};

// Newsletter Section
const NewsletterSection = () => {
	const { t, i18n } = useTranslation();
	const [email, setEmail] = useState("");
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!email) return;

		setLoading(true);
		try {
			await axios.post(`${API}/newsletter`, {
				email,
				language: i18n.language,
			});
			toast.success(t("newsletter.success"));
			setEmail("");
		} catch (error) {
			if (error.response?.status === 400) {
				toast.info(t("newsletter.already_subscribed"));
			} else {
				toast.error(t("newsletter.error"));
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<section
			className="py-20 md:py-24 bg-secondary"
			data-testid="newsletter-section"
		>
			<div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
				>
					<span className="text-gold font-accent text-lg tracking-widest">
						{t("newsletter.subtitle")}
					</span>
					<h2 className="font-heading text-3xl md:text-4xl text-primary mt-2 mb-4">
						{t("newsletter.title")}
					</h2>
					<p className="text-muted-foreground mb-8 font-body">
						{t("newsletter.description")}
					</p>

					<form
						onSubmit={handleSubmit}
						className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
					>
						<Input
							type="email"
							placeholder={t("newsletter.placeholder")}
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className="flex-grow rounded-none border-border py-6"
							required
							data-testid="newsletter-email-input"
						/>
						<Button
							type="submit"
							disabled={loading}
							className="bg-primary text-white hover:bg-primary/90 rounded-none px-8 py-6 text-sm tracking-wider uppercase"
							data-testid="newsletter-submit-btn"
						>
							{loading ? "..." : t("newsletter.submit")}
						</Button>
					</form>
				</motion.div>
			</div>
		</section>
	);
};

// CTA Section
const CTASection = () => {
	const { t } = useTranslation();

	return (
		<section
			className="py-20 md:py-32 bg-background relative overflow-hidden"
			data-testid="cta-section"
		>
			<div className="absolute inset-0 opacity-5">
				<img
					src="/images/plats/apero.jpeg"
					alt=""
					className="w-full h-full object-cover"
				/>
			</div>
			<div className="container mx-auto px-4 md:px-6 max-w-7xl relative">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					className="text-center"
				>
					<h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-primary mb-6">
						{t("reservation.subtitle")}
					</h2>
					<p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8 font-body">
						{t("reservation.description")}
					</p>

					<div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
						<Link to="/reservation">
							<Button
								size="lg"
								className="bg-gold hover:bg-gold/90 text-white rounded-none px-10 py-7 text-sm tracking-wider uppercase btn-hover-expand"
								data-testid="cta-reserve-btn"
							>
								<Calendar className="mr-2 h-5 w-5" />
								{t("hero.cta_reserve")}
							</Button>
						</Link>

						<a href="tel:+41223424243">
							<Button
								size="lg"
								variant="outline"
								className="border-primary text-primary hover:bg-primary hover:text-white rounded-none px-10 py-7 text-sm tracking-wider uppercase"
							>
								<Phone className="mr-2 h-5 w-5" />
								{t("common.phone_display")}
							</Button>
						</a>
					</div>
				</motion.div>
			</div>
		</section>
	);
};

// Main HomePage Component
const HomePage = () => {
	return (
		<div data-testid="home-page">
			<HeroSection />
			<DayTimelineSection />
			<AboutSection />
			<FeaturedDishes />
			<ReviewsSection />
			<NewsletterSection />
			<CTASection />
		</div>
	);
};

export default HomePage;
